import { AlertCircle, RefreshCw, WifiOff } from 'lucide-react';
import { btns } from './buttons';

/**
 * Shared "this query genuinely failed" state — distinct from an empty result.
 * Without this, list/detail pages that only check `isLoading` silently render
 * an empty-state message on a real fetch failure (network error, 5xx, etc.),
 * which is indistinguishable from "there's nothing here yet."
 */
export function QueryErrorState({
  // Callers should pass something specific. This default is the safety net, and
  // unlike the per-page messages it keeps its own call to action, since a
  // caller that omits `message` may also have omitted `onRetry` and left no
  // button to lean on.
  message = "We couldn't load this. Please try again.",
  onRetry,
}: {
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 text-center py-10 px-4">
      <AlertCircle size={20} strokeWidth={2} className="text-danger" aria-hidden />
      <p className="text-slate text-[13px] font-sans m-0">{message}</p>
      {onRetry && (
        <button onClick={onRetry} className={`inline-flex items-center gap-1.5 ${btns.btn}`}>
          <RefreshCw size={12} strokeWidth={2.25} aria-hidden />
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * The offline sibling of {@link QueryErrorState} — same centered layout, so the
 * five states a surface can be in (loading, offline, error, empty, ready) read
 * as one family rather than five ad-hoc treatments.
 *
 * Distinct from the error state on purpose. "Couldn't load your accounts" with
 * a Try again button invites a user with no connection to mash a button that
 * cannot work; naming the actual cause tells them the app is fine and their
 * data is safe, and that it will come back on its own. React Query resumes
 * paused queries automatically when the connection returns, so no user action
 * is required and the copy says so.
 *
 * `onRetry` is still offered because the browser's online signal reports
 * transport rather than reachability and is occasionally simply wrong; a manual
 * retry is the escape hatch when it is. It is harmless when the device really
 * is offline (the query just pauses again).
 */
export function OfflineState({
  message = "You're offline, so this can't load right now.",
  onRetry,
}: {
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 text-center py-10 px-4">
      <WifiOff size={20} strokeWidth={2} className="text-slate" aria-hidden />
      <p className="text-slate text-[13px] font-sans m-0 max-w-sm">
        {message} This page will refresh itself once your connection is back.
      </p>
      {onRetry && (
        <button onClick={onRetry} className={`inline-flex items-center gap-1.5 ${btns.btn}`}>
          <RefreshCw size={12} strokeWidth={2.25} aria-hidden />
          Try again
        </button>
      )}
    </div>
  );
}
